/**
 * Opening the remote console and the device console, each in its own tab.
 *
 * Both are separate DOM pages served by the same process, and both authenticate with the SAME
 * one-time ticket endpoint - what is being authorized is the same thing, this technician for this
 * device, and a second ticket type would be a second thing to get wrong.
 *
 * THREE DETAILS ARE LOAD-BEARING, all three learned the hard way in the OpenSilver client:
 *
 *   * **The blank tab is opened BEFORE the first await**, while the click is still a trusted user
 *     gesture. A pop-up opened after an async round trip is blocked, so minting the ticket first
 *     would make the button work in development and fail for every real technician.
 *   * **The ticket rides in the FRAGMENT.** Fragments are never sent to a server, so a single-use
 *     credential stays out of access logs, Referer headers and anything that records a URL.
 *   * **The `?s=` nonce is what makes a reused tab actually reload.** The tab is reused by name, and
 *     a URL differing from the loaded one only in its fragment is a *same-document* navigation: the
 *     browser changes the hash and stops. Nothing re-runs - so the console keeps whatever bundle it
 *     loaded first (a rebuilt console silently never reaching the technician, which made every
 *     console-side fix look like a no-op) and its `startSession` never redeems the new ticket.
 *
 * Separate tab handles per console, because a technician routinely wants both at once - which is
 * most of the reason the consoles are separate pages rather than more panels on the device page.
 */
import * as crm from './api/crm.js';
import { describeError } from './api/http.js';

/** What a launch did, for the caller's status line. `blocked` is the pop-up blocker. */
export type LaunchOutcome =
  | { ok: true }
  | { ok: false; blocked: true; message: string }
  | { ok: false; blocked: false; message: string };

function nonce(): string {
  // Eight hex characters is plenty to make each launch a distinct document; this is a cache-buster,
  // not a secret.
  return Math.random().toString(16).slice(2, 10);
}

async function launch(
  deviceId: number,
  handle: string,
  page: string,
  failure: string
): Promise<LaunchOutcome> {
  // See the note above: this must happen before the first await.
  const tab = window.open('', handle);
  if (!tab) {
    return { ok: false, blocked: true, message: 'Allow pop-ups for this site to open this console.' };
  }

  let ticket: string;
  try {
    ticket = (await crm.mintRemoteTicket(deviceId)).ticket;
  } catch (error) {
    // The tab was opened for a session that will not happen, so it is closed again rather than left
    // blank - an empty tab with no explanation reads as the console having crashed.
    try {
      tab.close();
    } catch {
      /* already gone */
    }
    return { ok: false, blocked: false, message: `${failure} ${describeError(error)}`.trim() };
  }

  const api = window.location.origin;
  const url =
    `${api}/${page}/index.html?s=${nonce()}` +
    `#device=${deviceId}` +
    `&ticket=${encodeURIComponent(ticket)}` +
    `&api=${encodeURIComponent(api)}`;

  try {
    tab.location.href = url;
    tab.focus();
    return { ok: true };
  } catch {
    return { ok: false, blocked: false, message: 'Could not open this console in this browser.' };
  }
}

/** Take control: the live picture, input, clipboard and file transfer. Needs the device online. */
export function openRemoteConsole(deviceId: number): Promise<LaunchOutcome> {
  return launch(
    deviceId,
    `jetsonRemote${deviceId}`,
    'remote',
    'Could not start a remote session for this device.'
  );
}

/**
 * The device console: actions, a shell, files and the event log, phone-first.
 *
 * Deliberately offered for an OFFLINE device too, unlike Take control. Half of what it carries works
 * against a machine that is off - reading the stored inventory, and Wake, which is sent by a
 * different machine entirely - so refusing to open it would withhold the one surface that still has
 * something to say.
 */
export function openDeviceConsole(deviceId: number): Promise<LaunchOutcome> {
  return launch(
    deviceId,
    `jetsonDevice${deviceId}`,
    'device',
    'Could not open the device console for this device.'
  );
}
